import { useRef, useState } from 'react';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import { IconButton } from '@mui/material';
import { ICustomFile } from 'entities';
import { IPostInputModel } from 'inputModels';
import { serialize } from 'object-to-formdata';
import { Controller, useForm } from 'react-hook-form';
import emojiData from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import i18n from '@emoji-mart/data/i18n/pt.json';
import { IPostViewModel } from 'viewModels';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { FeedPublication } from './styles';
import { Button } from '../../Buttons/Button';
import { PostType } from '../../../types/enums';
import { PostController } from '../../../controller/PostController';
import { useAuth } from '../../../context';
import { usePosts } from '../../../context/posts';
import { Attachment } from '../../Attachment';
import useClickOutside from '../../../hooks/useClickOutside';
import { ModalCheckInLocation } from '../../Modals/ModalCheckInLocation';
import { DropZone } from '../../DropZone';
import { CircularProgressWithLabel } from '../../CircularProgressWithLabel';

export function FeedPublicationTextArea() {
  const theme = useTheme();
  const { user } = useAuth();
  const { setPosts } = usePosts();
  const ref = useRef<HTMLDivElement>(null);
  const [isOpenEmojiPicker, setIsOpenEmojiPicker] = useState(false);
  const [isOpenCheckIn, setIsOpenCheckIn] = useState(false);
  const [attachments, setAttachments] = useState<ICustomFile[]>([]);
  const [textLength, setTextLength] = useState(0);

  const postSchema: yup.SchemaOf<IPostInputModel> = yup.object({
    body: yup.string(),
    type: yup
      .mixed<PostType>()
      .oneOf(Object.values(PostType) as number[])
      .required(),
    attachments: yup.array(),
    latitude: yup.number(),
    longitude: yup.number(),
  });

  const defaultValues: IPostInputModel = {
    body: '',
    type: PostType.OTHER,
    attachments: [],
  };

  const { handleSubmit, reset, control, getValues, setValue, watch } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const handleInsertPost = async (data: IPostInputModel) => {
    const input: IPostInputModel = {
      body: data.body,
      type: PostType.OTHER,
    };
    const formData = serialize(input, { indices: true });
    if (attachments.length > 0) {
      attachments.forEach((attachment) => {
        formData.append('attachments', attachment.file);
      });
    }
    await PostController.insert(formData).then((id) => {
      const post: IPostViewModel = {
        id,
        body: data.body,
        type: PostType.OTHER,
        attachments: attachments.map((attachment) => ({
          id: attachment.id,
          name: attachment.file.name,
          url: attachment.preview,
        })),
        createdAt: new Date(),
        createdBy: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          profilePictureId: user.profilePictureId,
        },
      };
      setPosts((prevState) => [post, ...prevState]);
      reset({ body: '' });
      setAttachments([]);
      setIsOpenEmojiPicker(false);
    });
  };

  const handleAddEmoji = () => (emoji: any) => {
    const { native } = emoji;
    const { body } = getValues();
    setValue('body', `${body}${native}`);
  };
  useClickOutside(() => {
    setIsOpenEmojiPicker(false);
  }, ref);

  const { body, longitude } = watch();

  return (
    <FeedPublication>
      <div className="feed-header">
        <Controller
          control={control}
          name="body"
          render={({ field: { value, onChange, ...rest } }) => (
            <textarea
              value={value}
              onChange={(e) => {
                onChange(e);
                setTextLength(e.target.value.length);
              }}
              {...rest}
              className="feed-text"
              placeholder="O que você quer compartilhar?"
              maxLength={500}
            />
          )}
        />
        <div className="containerAttachment">
          {attachments.map((attachment) => (
            <Attachment
              key={attachment.id}
              data={{
                file: attachment.file,
                name: attachment.file.name,
                id: attachment.id,
                type: attachment.file.type,
                uri: attachment.preview,
              }}
              options={{
                disableDelete: false,
                disableView: false,
                disableDownload: true,
              }}
              onDelete={() =>
                setAttachments((prevState) => prevState.filter((att) => att.id !== attachment.id))
              }
            />
          ))}
        </div>
      </div>
      <div className="feed-action">
        <div className="action">
          <DropZone
            multipleFiles
            renderType="button"
            type="picture"
            onFiles={(files) => setAttachments(files)}
          />
          <IconButton
            onClick={() => {
              setIsOpenCheckIn(true);
              setIsOpenEmojiPicker(false);
            }}
            size="small">
            <RoomOutlinedIcon style={{ fontSize: '28px', color: theme.colors.tertiary_500 }} />
          </IconButton>
          <IconButton onClick={() => setIsOpenEmojiPicker(!isOpenEmojiPicker)} size="small">
            <SentimentSatisfiedAltOutlinedIcon
              style={{ fontSize: '28px', color: theme.colors.tertiary_500 }}
            />
          </IconButton>
        </div>
        {!!textLength && (
          <div className="progress">
            <CircularProgressWithLabel
              variant="determinate"
              size={35}
              color={
                (textLength / 500) * 100 <= 85
                  ? 'success'
                  : (textLength / 500) * 100 < 100
                  ? 'info'
                  : 'primary'
              }
              value={(textLength / 500) * 100}
            />
          </div>
        )}
        <div>
          <Button
            className="publication"
            text="Publicar"
            onClick={handleSubmit(handleInsertPost)}
            disabled={body === '' && attachments.length === 0 && longitude === undefined}
            dimensions={{
              width: '10rem',
              height: '2rem',
            }}
          />
        </div>
      </div>
      {isOpenEmojiPicker && (
        <div ref={ref} className="containerEmojiPicker">
          <Picker data={emojiData} onEmojiSelect={handleAddEmoji()} i18n={i18n} />
        </div>
      )}
      <ModalCheckInLocation isOpen={isOpenCheckIn} onRequestClose={() => setIsOpenCheckIn(false)} />
    </FeedPublication>
  );
}
